import { Contract, ContractMethod, MethodInput } from '@/app/types/proposals';
import { proposalFlow } from './proposalFlow';
import { InputTypes, InputVariant, ValidationResult } from './types/inputTypes';

/**
 * Gets the list of contracts available for a specific proposal type
 * @param proposalType - The type of proposal (e.g., "constitutionalUpgrades")
 * @returns Array of contracts or empty array if proposal type not found
 */
export const getContractsForProposalType = (
  proposalType: keyof typeof proposalFlow.proposal,
): Contract[] => {
  const contracts = proposalFlow.proposal[proposalType]?.contracts;

  if (!contracts) return [];

  return contracts;
};

/**
 * Gets the list of methods for a specific contract within a proposal type
 * @param proposalType - The type of proposal
 * @param contractAddress - The address of the contract
 * @returns Array of methods or empty array if contract not found
 */
export const getMethodsForContract = (
  proposalType: string,
  contractAddress: string,
): ContractMethod[] => {
  const contract = proposalFlow.proposal[proposalType]?.contracts.find(
    (c) => c.address === contractAddress,
  );
  return contract?.methods || [];
};

/**
 * Gets the list of inputs for a specific method across all contracts
 * @param methodName - The name of the method to find
 * @returns Array of method inputs or undefined if method not found
 */
export const getMethodInputs = (methodName: string): MethodInput[] | undefined => {
  // Search through all proposal types and their contracts
  for (const proposalType of Object.values(proposalFlow.proposal)) {
    for (const contract of proposalType.contracts) {
      const method = contract.methods.find((m) => m.name === methodName);
      if (method) {
        return method.inputs;
      }
    }
  }
  return undefined;
};

// Optional: Additional helper to get method inputs for a specific contract
export const getMethodInputsForContract = (
  contract: Contract,
  methodName: string,
): MethodInput[] | undefined => {
  return contract.methods.find((m) => m.name === methodName)?.inputs;
};

export const getInputPlaceholder = (
  show: string,
  type: string,
  variant: InputVariant = 'default',
): string => {
  const inputType = InputTypes[type];
  if (!inputType) {
    return `Enter value for ${show}`;
  }

  return inputType.placeholder(show, variant);
};

export const getProposalTypeKey = (displayName: string): string => {
  const words = displayName.replace(/[^a-zA-Z\s]/g, '').split(' ');

  return words
    .map((word, index) => {
      if (index === 0) {
        return word.toLowerCase();
      }

      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join('');
};

export const generateId = () =>
  Math.random().toString(36).substring(2) + Date.now().toString(36);

export const buttonClass = (disabled: boolean, additionalClasses = '') =>
  `py-2 px-4 rounded-md whitespace-nowrap ${additionalClasses} ${
    disabled
      ? 'cursor-not-allowed bg-neutral-700 text-neutral-200'
      : ` max-md:border max-md:border-neutral-700 md:bg-darkbtn hover:bg-darkbtnhover text-neutral-50`
  }`;

export const transformInput = (input: MethodInput, value: string): MethodInput => {
  const inputType = InputTypes[input.type];
  if (!inputType) {
    return {
      ...input,
      value,
      internalValue: value,
    };
  }

  return {
    ...input,
    value,
    internalValue: inputType.transform(value, input.variant || 'default'),
  };
};

export const validateInput = (
  type: string,
  value: string,
  variant: InputVariant = 'default',
): ValidationResult => {
  if (!value.trim()) {
    return { isValid: false, error: 'This field is required' };
  }

  const inputType = InputTypes[type];
  if (!inputType) {
    return { isValid: true };
  }

  return inputType.validate(value, variant);
};
